/**
 * console 로그인 — dashboard와 동일한 /auth/login endpoint 호출.
 * 단 응답 console_enabled = false 면 /access-denied 로 이동 (토큰은 저장 X).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import type { LoginRequest, LoginResponse } from '@atgrd/shared';
import { SERVER_URL, STORAGE_KEY_SESSION_TOKEN, STORAGE_KEY_OWNER_ID } from '@/config';

export function LoginPage() {
  const nav = useNavigate();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${SERVER_URL}/api/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, password } satisfies LoginRequest),
      });
      const json = (await res.json()) as LoginResponse;
      if (!json.ok || !json.session_token || !json.owner_id) {
        setError('전화번호 또는 비밀번호가 일치하지 않습니다.');
        return;
      }
      if (!json.console_enabled) {
        // 토큰 저장 안 함 — console_enabled 없는 계정 흔적 0
        nav('/access-denied', { replace: true });
        return;
      }
      localStorage.setItem(STORAGE_KEY_SESSION_TOKEN, json.session_token);
      localStorage.setItem(STORAGE_KEY_OWNER_ID, json.owner_id);
      nav('/', { replace: true });
    } catch (err) {
      setError((err as Error).message ?? '로그인 실패');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-alt p-6">
      <div className="w-full max-w-sm bg-surface border border-bdr-subtlest rounded-xl p-7">
        <header className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-md bg-fg flex items-center justify-center">
              <span className="text-surface text-xs font-bold">C</span>
            </div>
            <span className="font-bold">ATGRD 설정콘솔</span>
          </div>
          <p className="text-xs text-fg-subtler">
            점주(특히 프랜차이즈 본사) 전용. ATGRD 허가 계정만 접근 가능합니다.
          </p>
        </header>

        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <span className="block text-xs font-semibold text-fg-subtle mb-1">전화번호</span>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="010-1234-5678"
              className="w-full px-3 py-2 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              autoComplete="username"
              required
            />
          </label>

          <label className="block">
            <span className="block text-xs font-semibold text-fg-subtle mb-1">비밀번호</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              autoComplete="current-password"
              required
            />
          </label>

          {error && (
            <div className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-900 text-xs">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full mt-2 px-4 py-2 rounded-md text-sm font-semibold bg-fg text-surface hover:bg-fg/90 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? '로그인 중...' : '로그인'}
          </button>
        </form>

        <p className="text-[11px] text-fg-subtler mt-5 leading-relaxed">
          접근 권한이 필요하면 ATGRD(<a className="underline" href="mailto:purchase@autogeared.com">purchase@autogeared.com</a>)에 문의해 주세요.
        </p>
      </div>
    </div>
  );
}
